/*
 * model-factory.js — entity-item-model-factory: turns an entity DESCRIPTOR
 * (a kind config: { fields, itemCfg, settings }) into a live runtime record
 * Model class (recovered `b(entityDescriptor)` from entity-item-model-factory.js).
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (defaults per field from params.defaultValue -> params.defaultValueGetter ->
 * registry default(); validation per field = field.validationRules || registry
 * validation, ANDed with `required` when field.required; routes/paramsDecorators/
 * responseDecorators delegated to descriptor.itemCfg; the "employee/read[value]"
 * result-key suffix convention; save() -> itemCfg.routes()[create|update]),
 * internal impl re-authored. Matches ENTITY-BUILDER.md §(4) and §8.
 */

import { Model } from './model.js';
import { fieldDefault, fieldValidation, fieldTypeDef } from './field-types.js';
import { request } from './entity-api.js';

/** Resolve the descriptor's field-descriptor array from its many shapes. */
export function resolveFields(descriptor) {
  if (!descriptor) return [];
  const f = descriptor.fields;
  if (Array.isArray(f)) return f;
  if (f && Array.isArray(f.models)) return f.models.map((m) => (typeof m.toObject === 'function' ? m.toObject() : m));
  if (typeof descriptor.getFields === 'function') return descriptor.getFields() || [];
  return [];
}

/** Parse the "action[resultKey]" convention -> { action, resultKey }. */
export function parseRoute(route) {
  if (!route) return { action: route, resultKey: undefined };
  const m = String(route).match(/^(.*?)\[(\w+)\]$/);
  return m ? { action: m[1], resultKey: m[2] } : { action: route, resultKey: undefined };
}

/** Flatten a server record's `fields:{id:{value}}` map into flat props. */
export function unwrapFields(record) {
  if (!record || typeof record !== 'object') return record;
  if (!record.fields || typeof record.fields !== 'object' || Array.isArray(record.fields)) return record;
  const out = {};
  Object.keys(record).forEach((k) => { if (k !== 'fields') out[k] = record[k]; });
  Object.keys(record.fields).forEach((id) => {
    const cell = record.fields[id];
    out[id] = cell && typeof cell === 'object' && 'value' in cell ? cell.value : cell;
  });
  return out;
}

/** Re-wrap flat props back into `{ id, fields:{id:{value}} }` on save. */
export function wrapFields(flat, fields) {
  const wrapped = { fields: {} };
  if (flat.id != null) wrapped.id = flat.id;
  fields.forEach((f) => {
    if (f && f.id != null && f.embedded !== true && flat[f.id] !== undefined) {
      wrapped.fields[f.id] = { value: flat[f.id] };
    }
  });
  return wrapped;
}

/** Decode reference-type values (employee/multi_employee/file/image) on load. */
function decodeReferenceTypes(record, fields, ctx) {
  if (!record || typeof record !== 'object') return record;
  fields.forEach((f) => {
    const def = fieldTypeDef(f && f.type);
    if (def && typeof def.decode === 'function' && record[f.id] !== undefined) {
      record[f.id] = def.decode(record[f.id], Object.assign({ item: record }, ctx));
    }
  });
  return record;
}

/**
 * buildEntityItemModel(descriptor, options) -> a Model subclass.
 * options:
 *   storeGetters   — for params.defaultValueGetter resolution
 *   decodeContext  — { employeeById, employeeList, … } for reference decode
 */
export function buildEntityItemModel(descriptor, options = {}) {
  const fields = resolveFields(descriptor);
  const itemCfg = (descriptor && descriptor.itemCfg) || {};
  const storeGetters = options.storeGetters || null;
  const decodeCtx = options.decodeContext || {};

  const routesOf = () => (typeof itemCfg.routes === 'function' ? itemCfg.routes.call(descriptor) : {});
  const paramsOf = () => (typeof itemCfg.paramsDecorators === 'function' ? itemCfg.paramsDecorators.call(descriptor) : {});
  const responseOf = () => (typeof itemCfg.responseDecorators === 'function' ? itemCfg.responseDecorators.call(descriptor) : {});

  class ItemModel extends Model {
    defaults() {
      const out = { id: null };
      for (const f of fields) if (f && f.id != null) out[f.id] = fieldDefault(f, storeGetters);
      return out;
    }

    validation() {
      const out = {};
      for (const f of fields) {
        if (!f || f.id == null) continue;
        const rule = fieldValidation(f);
        if (rule) out[f.id] = rule;
      }
      return out;
    }

    /* itemCfg passthroughs (used by the store's applyAvatar + callers) */
    routes() { return routesOf(); }
    paramsDecorators() { return paramsOf(); }
    responseDecorators() { return responseOf(); }
    getFields() { return fields; }
    getDescriptor() { return descriptor; }

    /** new Model({id}).fetch() — load one record and decorate it. */
    async fetch() {
      const routes = routesOf();
      const spec = parseRoute(routes.fetch || 'entity/read');
      const paramsDec = paramsOf();
      const params = typeof paramsDec.fetch === 'function'
        ? paramsDec.fetch.call(descriptor, this.toObject())
        : { id: this.id };
      let resp = await request(spec.action, params, spec.resultKey);
      resp = unwrapFields(resp);
      if (typeof responseOf().fetch === 'function') resp = responseOf().fetch.call(descriptor, resp) || resp;
      resp = decodeReferenceTypes(resp, fields, decodeCtx);
      if (resp && typeof resp === 'object') this.set(resp);
      this.resetChanges();
      return this;
    }

    /** save() -> itemCfg.routes()[create|update] with paramsDecorators body. */
    async save() {
      const routes = routesOf();
      const isNew = this.isNew();
      const spec = parseRoute(isNew ? routes.create : routes.update);
      const paramsDec = paramsOf();
      const key = isNew ? 'create' : 'update';
      let params;
      if (typeof paramsDec[key] === 'function') {
        params = paramsDec[key].call(descriptor, this.toObject());
      } else {
        // generic custom-entity fallback: re-wrap flat props into fields{id:{value}}
        params = { entity_id: descriptor && descriptor.id, item: wrapFields(this.getSaveData(), fields) };
      }
      let resp = await request(spec.action, params, spec.resultKey);
      if (typeof responseOf()[key] === 'function') resp = responseOf()[key].call(descriptor, resp) || resp;
      if (resp && resp.id != null) this.set('id', resp.id);
      this.resetChanges();
      return resp;
    }

    /** delete() -> itemCfg.routes().delete. */
    async remove() {
      const routes = routesOf();
      const spec = parseRoute(routes.delete);
      const paramsDec = paramsOf();
      const params = typeof paramsDec.delete === 'function'
        ? paramsDec.delete.call(descriptor, this.toObject())
        : { id: this.id };
      return request(spec.action, params, spec.resultKey);
    }
  }

  return ItemModel;
}

export default buildEntityItemModel;
